import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useDispatch } from "react-redux";
import { AestraPrimaryCareTable } from "../../../data-model/aestraPrimaryCareTable";
import { ModalId } from "../../../enums/modalId";
import { useAbortableFetch } from "../../../hooks/useAbortableFetch";
import { useModalOpener } from "../../../hooks/useModalOpener";
import {
  closeModal,
  setOpenedModalId,
} from "../../../redux/slice/modalSliceNew";
import { AppDispatch } from "../../../redux/store/store";
import { onSendToPlanConfirm } from "../actionHandlers/onSendToPlanConfirm";
import {
  handleConfirmRerun,
  handleOpenRerunModal,
} from "../actionHandlers/rerunModalHandlers";
import { useModalStates } from "./useModalStates";

export const usePrimaryCareModals = (
  tableData: AestraPrimaryCareTable[],
  userEmail: string,
  resetAndFetchOrders: () => void,
) => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    sendToPlanModalType,
    setSendToPlanModalType,
    openRerunModal,
    setOpenRerunModal,
    rerunType,
    setRerunType,
    rerunOrderData,
    setRerunOrderData,
    selectedOrderIdForRerun,
    setSelectedOrderIdForRerun,
    openReportPrescriptionModal,
    setOpenReportPrescriptionModal,
    selectedOrderIdForReportPrescription,
    setSelectedOrderIdForReportPrescription,
    openReportMedicationModal,
    setOpenReportMedicationModal,
    selectedOrderIdForReportMedication,
    setSelectedOrderIdForReportMedication,
    closeRerunModal,
    closeReportPrescriptionModal,
    closeReportMedicationModal,
  } = useModalStates();

  const { openedModalId } = useModalOpener({
    openRerunModal,
    openReportPrescriptionModal,
    openReportMedicationModal,
  });

  const { runFetch: runSendToPlanConfirm } =
    useAbortableFetch(onSendToPlanConfirm);

  const handleSendToPlanModalOpen = (
    orderId: string,
    modalType: "send_to_plan" | "retry_qa" | "fetch_qa" = "send_to_plan",
    selectedRowForSendToPlan: React.Dispatch<
      React.SetStateAction<AestraPrimaryCareTable | null>
    >,
  ) => {
    const rowData = tableData.find((row) => row.identifier === orderId);
    if (rowData) {
      selectedRowForSendToPlan(rowData);
      setSendToPlanModalType(modalType);
      dispatch(
        setOpenedModalId({
          id: ModalId.SEND_TO_PLAN_CONFIRM_MODAL,
          metaData: { rowData },
        }),
      );
    }
  };

  const handleSendToPlanConfirm = (
    selectedRowForSendToPlan: AestraPrimaryCareTable | null,
  ) => {
    if (!selectedRowForSendToPlan) return;

    runSendToPlanConfirm({
      identifier: selectedRowForSendToPlan.identifier,
      email: userEmail,
    });

    setTimeout(() => {
      dispatch(closeModal());
      toast.success("send-to-plan-initiated");
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }, 5000);
  };

  const handleRerunModalOpen = (orderId: string, type: string) => {
    handleOpenRerunModal(
      orderId,
      type,
      tableData,
      setRerunOrderData,
      setSelectedOrderIdForRerun,
      setRerunType,
      setOpenRerunModal,
    );
  };

  const handleRerunConfirm = async () => {
    await handleConfirmRerun(
      selectedOrderIdForRerun,
      rerunType,
      resetAndFetchOrders,
      closeRerunModal,
    );
  };

  const handleReportPrescriptionModalOpen = (orderId: string) => {
    setSelectedOrderIdForReportPrescription(orderId);
    setOpenReportPrescriptionModal(true);
  };

  const handleReportMedicationModalOpen = (orderId: string) => {
    setSelectedOrderIdForReportMedication(orderId);
    setOpenReportMedicationModal(true);
  };

  return {
    openedModalId,
    sendToPlanModalType,
    openRerunModal,
    rerunType,
    rerunOrderData,
    selectedOrderIdForRerun,
    openReportPrescriptionModal,
    selectedOrderIdForReportPrescription,
    openReportMedicationModal,
    selectedOrderIdForReportMedication,
    handleSendToPlanModalOpen,
    handleSendToPlanConfirm,
    handleRerunModalOpen,
    handleRerunConfirm,
    handleReportPrescriptionModalOpen,
    handleReportMedicationModalOpen,
    closeRerunModal,
    closeReportPrescriptionModal,
    closeReportMedicationModal,
  };
};
